import MapView from '@arcgis/core/views/MapView';
import { Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import * as uuid from 'uuid';

import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  ViewContainerRef,
} from '@angular/core';

import { SnLonLat, SnLonLatBounds } from '@lib/geo/helpers';

import { EsriTheme } from '../../enum/esri-theme.enum';
import { IEsriMapConstraints, IEsriMapOptions } from '../../interfaces';
import { EsriMap } from '../../map/esri-map';
import { EsriTileMonitorService } from '../../services/esri-tile-monitor.service';

@Component({
  selector: 'fs-esri-map',
  templateUrl: './esri-map.component.html',
  styleUrls: ['./esri-map.component.scss'],
  host: {
    class: 'sn-map sn-google-map',
  },
})
export class EsriMapComponent implements AfterViewInit, OnDestroy {
  @Input() public mapOptions: IEsriMapOptions = {
    center: [-84.244, 34.052],
    zoom: 20,
  };

  @Input() public mapConstraints: IEsriMapConstraints = {
    minZoom: 2,
    maxZoom: 23,
    snapToZoom: true,
    rotationEnabled: false,
  };

  @Input() public esriMapTheme: EsriTheme;

  @Output() public readonly mapInit = new EventEmitter<EsriMap>();
  @Output() public readonly mapClick = new EventEmitter<SnLonLat>();
  @Output() public readonly mapDblClick = new EventEmitter<SnLonLat>();
  @Output() public readonly mapDragStart = new EventEmitter<void>();
  @Output() public readonly mapDrag = new EventEmitter<void>();
  @Output() public readonly mapDragEnd = new EventEmitter<void>();
  @Output() public readonly centerChange = new EventEmitter<SnLonLat>();
  @Output() public readonly boundsChange = new EventEmitter<SnLonLatBounds>();
  @Output() public readonly zoomChange = new EventEmitter<number>();

  public id: string = uuid.v4();
  public container: string;

  private _mapInstance: EsriMap;
  private _mapViewInstance: MapView;

  private readonly _destroy$ = new Subject<void>();

  constructor(
    private readonly _viewContainerRef: ViewContainerRef,
    private readonly _esriTileMonitorService: EsriTileMonitorService,
  ) {
    this.container = `esri-map-${this.id}`;
  }

  public ngAfterViewInit(): void {
    // Initialize tile monitor service
    this._esriTileMonitorService.initialize();

    this._mapInstance = new EsriMap(
      this.mapOptions,
      this.mapConstraints,
      this.esriMapTheme,
      this._viewContainerRef,
      this.id,
    );

    this._mapViewInstance = this._mapInstance.mapView;

    this._watchMap();

    this._mapInstance
      .on('idle')
      .pipe(take(1), takeUntil(this._destroy$))
      .subscribe(() => {
        this.mapInit.emit(this._mapInstance);
        this.mapInit.complete();
      });
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  private _watchMap(): void {
    this._mapInstance
      .on('click')
      .pipe(takeUntil(this._destroy$))
      .subscribe(this.mapClick.emit.bind(this.mapClick));

    this._mapInstance
      .on('dblclick')
      .pipe(takeUntil(this._destroy$))
      .subscribe(this.mapDblClick.emit.bind(this.mapDblClick));

    this._mapInstance
      .on('dragstart')
      .pipe(takeUntil(this._destroy$))
      .subscribe(this.mapDragStart.emit.bind(this.mapDragStart));

    this._mapInstance
      .on('drag')
      .pipe(takeUntil(this._destroy$))
      .subscribe(this.mapDrag.emit.bind(this.mapDrag));

    this._mapInstance
      .on('dragend')
      .pipe(takeUntil(this._destroy$))
      .subscribe(this.mapDragEnd.emit.bind(this.mapDragEnd));

    this._mapInstance
      .on('centerChange')
      .pipe(takeUntil(this._destroy$))
      .subscribe(this.centerChange.emit.bind(this.centerChange));

    this._mapInstance
      .on('boundsChange')
      .pipe(takeUntil(this._destroy$))
      .subscribe(this.boundsChange.emit.bind(this.boundsChange));

    this._mapInstance
      .on('zoomChange')
      .pipe(takeUntil(this._destroy$))
      .subscribe(this.zoomChange.emit.bind(this.zoomChange));
  }
}
